import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Screen, TopBar, Loader } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { contentApi, EDITABLE_CATEGORIES } from "@/data/api";
import { Field, TextField, Toggle, SaveBar, useAdminStyles, listToText, textToList } from "@/components/adminui";

const EMPTY = { category: "ready", title: "", description: "", caption: "", idea: "", facts: [], spiritual: [], tip: "", imageUrl: "", fileUrl: "", isNew: false, featured: false, archived: false };

export default function AdminContentEdit() {
  const { colors } = useTheme();
  const s = useAdminStyles();
  const navigate = useNavigate();
  const { token } = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    contentApi.adminGet(id, token).then((d) => setForm({ ...EMPTY, ...d })).catch(() => toast.show("Couldn't load")).finally(() => setLoading(false));
  }, [id, token]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.show("Title is required"); return; }
    setSaving(true);
    const body = { ...form };
    try {
      if (id) await contentApi.adminUpdate(id, body, token);
      else await contentApi.adminCreate(body, token);
      toast.show("Saved ✨");
      navigate("/admin/content");
    } catch (e) { toast.show(e.message || "Couldn't save"); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!id || !window.confirm("Delete this content item?")) return;
    try { await contentApi.adminDelete(id, token); toast.show("Deleted"); navigate("/admin/content"); } catch (e) { toast.show(e.message || "Couldn't delete"); }
  };

  if (loading) return <Screen><TopBar title="Edit content" /><Loader /></Screen>;

  return (
    <Screen>
      <TopBar title={id ? "Edit content" : "New content"} />
      <div className="ritzy-fade" style={{ padding: "16px 20px 20px" }}>
        <Field label="Category">
          <select data-testid="content-category" value={form.category} onChange={(e) => set("category")(e.target.value)} style={s.input}>
            {EDITABLE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Title"><TextField value={form.title} onChange={set("title")} data-testid="content-title" placeholder="e.g. Amethyst starter post" /></Field>
        <Field label="Caption" hint="The ready-to-copy caption reps can post"><TextField multiline value={form.caption} onChange={set("caption")} data-testid="content-caption" /></Field>
        <Field label="Idea / hook"><TextField multiline rows={2} value={form.idea} onChange={set("idea")} /></Field>
        <Field label="Facts (one per line)"><TextField multiline value={listToText(form.facts)} onChange={(v) => set("facts")(textToList(v))} /></Field>
        <Field label="Spiritual notes (one per line)"><TextField multiline value={listToText(form.spiritual)} onChange={(v) => set("spiritual")(textToList(v))} /></Field>
        <Field label="Tip"><TextField multiline rows={2} value={form.tip} onChange={set("tip")} /></Field>
        <Field label="Description"><TextField multiline rows={2} value={form.description} onChange={set("description")} /></Field>
        <Field label="Image URL"><TextField value={form.imageUrl} onChange={set("imageUrl")} placeholder="https://..." /></Field>
        <Field label="File / link URL"><TextField value={form.fileUrl} onChange={set("fileUrl")} placeholder="https://..." /></Field>
        <Toggle testId="content-isnew" label="Mark as new" checked={form.isNew} onChange={set("isNew")} />
        <Toggle testId="content-featured" label="Feature as this week's focus" checked={form.featured} onChange={set("featured")} />
        <Toggle testId="content-archived" label="Archived (hidden from reps)" checked={form.archived} onChange={set("archived")} />
        <SaveBar onSave={save} saving={saving} onDelete={id ? del : undefined} />
      </div>
    </Screen>
  );
}
